import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpInterceptor,
  HttpErrorResponse,
  HttpEvent,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthenticationService, ToastService } from '../services';

/**
 * Public endpoints (login, register, OTP, forgot / reset password). Only
 * endpoints that are NEVER called by a signed-in screen belong here. For these:
 *  - no Authorization header is sent — a stale token in storage would
 *    otherwise make the backend reject the request with "token not valid"
 *    before it even checks the credentials;
 *  - a 401/403 is the endpoint's own answer (e.g. "Invalid Credentials"),
 *    NOT an expired session, so the page shows its own single message.
 */
const PUBLIC_ENDPOINTS = [
  '/user/login/',
  '/api/user/api/login/',
  '/api/user/api/system-admin/login/',
  '/user/register/',
  '/user/signup-ticket/',
  '/api/user/role-user/register/',
  '/api/candidate/register/',
  '/api/candidate/sales_candidate/register/',
  '/api/candidate/send-otp/',
  '/user/verify-otp/',
  '/user/forgot-password/',
  '/api/user/forgot-password/',
  '/user/reset-password/',
  '/api/user/reset-password/',
  '/test/reset-password/',
];

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private authenticationService: AuthenticationService,
    private toast: ToastService,
    private router: Router,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const isPublic = this.isPublicEndpoint(request.url);
    const token = localStorage.getItem('access_token');
    const sentToken = !!token && !isPublic;

    const modifiedRequest = sentToken
      ? request.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : request;

    return next.handle(modifiedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (!isPublic && !this.onPublicPage() && this.sessionEnded(error, sentToken)) {
          this.authenticationService.expireSession(error.error?.message || undefined);
        }
        return throwError(() => error);
      }),
    );
  }

  private isPublicEndpoint(url: string): boolean {
    const path = (url || '').split('?')[0];
    return PUBLIC_ENDPOINTS.some((p) => path.includes(p));
  }

  /** Login / register / reset pages: a 401 there is never "session expired". */
  private onPublicPage(): boolean {
    const url = this.router.url || '';
    return url.startsWith('/auth') || url === '/';
  }

  /**
   * - Token sent and the backend says it's no longer valid → session expired.
   * - No token at all on a protected call (storage cleared / another tab
   *   logged out) → the session is gone too.
   * Wrong username/password never gets here: login endpoints are public.
   */
  private sessionEnded(error: HttpErrorResponse, sentToken: boolean): boolean {
    const body = error.error || {};
    if (body?.error === 'session_expired') return true;
    if (error.status !== 401) return false;
    if (!sentToken) return true;
    return (
      body?.code === 'token_not_valid' ||
      body?.detail === 'Given token not valid for any token type' ||
      body?.detail === 'Authentication credentials were not provided.' ||
      body?.detail === 'User not found' ||
      body?.code === 'user_not_found'
    );
  }
}
